import { toStringFormatterFromLocale } from './metrics-formatter'
import { isNil } from './object'

const formatter = toStringFormatterFromLocale('en-US')

export const formatSigDigits = (value: number | null | undefined) =>
  isNil(value) ? '--' : formatter(value)

export const numsFromTo = (
  lowerBound: number,
  upperBound: number,
  { interval }: { interval: number } = { interval: 1 }
) => {
  const range = upperBound - lowerBound
  const length = 1 + range / interval

  return range > 0 ? Array.from({ length }, (_, i) => lowerBound + i * interval) : []
}

/**
 * Rounds the maximum of two numbers
 */
export const roundMax = (a: number, b: number) => Math.round(Math.max(a, b))

/**
 * Rounds the minimum of two numbers
 */
export const roundMin = (a: number, b: number) => Math.round(Math.min(a, b))

/**
 * Sums array of numbers
 */
export function sum(items: number[]) {
  return items.reduce((sum, next) => sum + next, 0)
}

/**
 * Sums array of items, using the accessor function to access the value to sum in each item
 */
export function sumBy<T>(items: readonly T[], accessor: (item: T) => number) {
  return items.reduce((sum, next) => sum + accessor(next), 0)
}

/**
 * Gives the minimum value, given the accessor (not the object that
 * contains the value, since there may be more than one).
 */
export function minBy<T>(arr: T[], accessor: (n: T) => number) {
  return Math.min(...arr.map(accessor))
}

/**
 * Gives the maximum value, given the accessor (not the object that
 * contains the value, since there may be more than one).
 */
export function maxBy<T>(arr: T[], accessor: (n: T) => number) {
  return Math.max(...arr.map(accessor))
}

/**
 * Returns the average of the numbers in the array
 */
export const avg = (nums: number[]) => nums.reduce((next, acc) => next + acc, 0) / nums.length

/**
 * Using the key returned by the accessor on each element of the array,
 * finds how often each key occurs, and returns object with the count of each key
 */
export function countByKey<T>(arr: T[], keyAccessor: (n: T) => string | number) {
  return arr.reduce(
    (acc, next) => {
      const key = keyAccessor(next)
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>
  )
}

/**
 * Finds the mode of the array, given the accessor (not the object that
 * contains the value, since there may be more than one). I.e., it finds
 * which (accessed) value occurred most often in the array.
 *
 * Returns an object with the value and the count of how often it occurred.
 * - If the array is empty, returns `{ value: undefined, count: 0 }`
 * - If there is a tie, returns the first value that tied
 */
export function modeBy<T, U>(arr: T[], keyAccessor: (n: T) => U) {
  const counts = arr.reduce((acc, next) => {
    const key = keyAccessor(next)
    acc.set(key, (acc.get(key) ?? 0) + 1)
    return acc
  }, new Map<U, number>())

  const response: { value: U | undefined; count: number } = {
    value: undefined,
    count: 0
  }

  counts.forEach((count, value) => {
    if (count > response.count) {
      response.value = value
      response.count = count
    }
  })
  return response
}

/**
 * Converts a value to a number, or returns the default value if it can't be converted
 */
export const toNumberOr = (value: unknown, defaultValue: number) => {
  const number = Number(value)
  return Number.isNaN(number) ? defaultValue : number
}

/**
 * Converts angle in degrees to radians
 */
export const degreesToRadians = (value: number) => (value * Math.PI) / 180

/**
 * Converts angle in radians to degrees
 */
export const radiansToDegrees = (value: number) => (value * 180) / Math.PI

/**
 * Checks if a number is a positive integer
 */
export const isPositiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0
